import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PatientList from './pages/PatientList';
import ScribeSession from './pages/ScribeSession';
import Drafts from './pages/Drafts';
import SessionPreview from './pages/SessionPreview';
import ProtocolLibrary from './pages/ProtocolLibrary';
import ProtocolWorkbench from './pages/ProtocolWorkbench';
import ReviewNote from './pages/ReviewNote';
import CustomApi from './pages/CustomApi';
import ProviderList from './pages/ProviderList';
import StaffList from './pages/StaffList';
import AdministratorList from './pages/AdministratorList';
import ManagePatients from './pages/ManagePatients';
import AddPatient from './pages/AddPatient';
import Settings from './pages/Settings';
import InviteSetup from './pages/InviteSetup';
import Activate from './pages/Activate';
import PortalSwitch from './pages/PortalSwitch';
import QueuePage from './pages/QueuePage';
import AppointmentRequests from './pages/AppointmentRequests';
import Insights from './pages/Insights';
import Credits from './pages/Credits';
// OrgDashboard is rendered by Dashboard at "/" — an org admin's home is the rollup.
import NodeCredits from './pages/NodeCredits';
import NodePeople from './pages/NodePeople';
import NodePatients from './pages/NodePatients';
import PrintVisit from './pages/PrintVisit';
import Reports from './pages/Reports';
import Notifications from './pages/Notifications';
import NodeDetail from './pages/NodeDetail';
import NodeAdmins from './pages/NodeAdmins';
import OrgSites from './pages/OrgSites';
import OrgTeam from './pages/OrgTeam';
import OrgAdmins from './pages/OrgAdmins';
import ForcePasswordChange from './pages/ForcePasswordChange';

/** Full-screen spinner shown while the server is being asked whether this browser has
 *  a session — it lives in a cookie the page cannot read, so there is nothing to check
 *  locally. Without this gate, the first (pre-auth) render redirects to /login, which
 *  then bounces an authenticated user to "/" — so a page reload always lands on home
 *  instead of the page you were on. */
function AuthLoading() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

// Roles whose session is scoped to a LEVEL of the hierarchy rather than to one
// clinic. A clinic-scoped screen has nothing to load for them until they open an
// office. Declared above PrivateRoute because that guard reads it.
const NODE_ROLES = ['enterprise_admin', 'org_admin', 'region_admin'];

function PrivateRoute({ children, requireAdmin, requireAdminOrStaff, requireProvider, requireOrgAdmin, requireSiteManager, requireNodeAdmin }) {
  const { user, isAuthenticated, loading, mustChangePassword } = useAuth();
  const location = useLocation();

  // Wait for the session to be confirmed before deciding — otherwise a reload bounces
  // through /login and loses the current route (lands on the home page).
  if (loading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    // Remember where they were headed. Emailed deep links (e.g. /protocols/:id) arrive
    // logged out; without this they sign in and land on the dashboard instead.
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  // An administrator set this person's password: nothing else works server-side until
  // they choose their own, so the whole app is replaced by that one screen.
  if (mustChangePassword) {
    return <ForcePasswordChange />;
  }

  if (requireAdmin && user?.role !== 'clinic_admin') {
    return <Navigate to="/" replace />;
  }

  if (requireAdminOrStaff && user?.role !== 'clinic_admin' && user?.role !== 'staff') {
    return <Navigate to="/" replace />;
  }

  if (requireProvider && user?.role !== 'provider' && user?.role !== 'clinic_admin') {
    return <Navigate to="/" replace />;
  }

  if (requireOrgAdmin && user?.role !== 'org_admin') {
    return <Navigate to="/" replace />;
  }

  // The node screens serve all three levels; which one you get is decided by the
  // session, so the guard only has to keep clinic-scoped roles out.
  if (requireNodeAdmin && !NODE_ROLES.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  // The sites roster is the one org screen a clinic admin can reach, and only from
  // their organisation's MAIN site — the practice the account was created from. An
  // admin of a branch opened later runs that branch, not the group. `org_is_top_site`
  // is a hint from the token; /api/v1/org re-checks it against the database before
  // letting anything actually be created.
  if (requireSiteManager
      && user?.role !== 'org_admin'
      && !user?.platform_entry
      && !(user?.role === 'clinic_admin' && user?.org_is_top_site)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Super admin uses platform APIs; clinic routes (patients, scribe, …) return 403 —
 * redirect home. A node-level admin is in the same position: their session names an
 * enterprise, organisation or region rather than a clinic, so a clinic-scoped screen
 * has nothing to load. They open an office first, which hands them a clinic session.
 */
function ClinicOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user?.role === 'super_admin' || NODE_ROLES.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  // An already-signed-in user who lands on /login (e.g. via an emailed deep link in a
  // second tab) should still be taken to the page they asked for.
  const afterLogin = location.state?.from && !location.state.from.startsWith('/login')
    ? location.state.from
    : '/';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Toaster position="top-right" />
      <Routes>
        <Route
          path="/login"
          element={loading ? <AuthLoading /> : (isAuthenticated ? <Navigate to={afterLogin} replace /> : <Login />)}
        />
        <Route path="/invite/setup" element={<InviteSetup />} />
        <Route path="/activate" element={<Activate />} />
        {/* Receives a one-time handoff code from another portal's Switch Portal. */}
        <Route path="/switch" element={<PortalSwitch />} />
        {/* Outside the Layout on purpose: the printed sheet is the whole page. */}
        <Route path="/print/visit/:sessionId"
          element={<PrivateRoute requireProvider><PrintVisit /></PrivateRoute>} />
        <Route 
          path="/" 
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Dashboard />} />
          {/* Organisation admin: several sites under one customer account. */}
          <Route
            path="node/credits"
            element={<PrivateRoute requireNodeAdmin><NodeCredits /></PrivateRoute>}
          />
          <Route
            path="node/people"
            element={<PrivateRoute requireNodeAdmin><NodePeople /></PrivateRoute>}
          />
          <Route
            path="node/patients"
            element={<PrivateRoute requireNodeAdmin><NodePatients /></PrivateRoute>}
          />
          <Route
            path="node/admins"
            element={<PrivateRoute requireNodeAdmin><NodeAdmins /></PrivateRoute>}
          />
          <Route
            path="node/:scope/:id"
            element={<PrivateRoute requireNodeAdmin><NodeDetail /></PrivateRoute>}
          />
          <Route
            path="org/sites"
            element={<PrivateRoute requireSiteManager><OrgSites /></PrivateRoute>}
          />
          <Route
            path="org/team"
            element={<PrivateRoute requireOrgAdmin><OrgTeam /></PrivateRoute>}
          />
          <Route
            path="org/credits"
            element={<Navigate to="/node/credits" replace />}
          />
          <Route
            path="org/admins"
            element={<PrivateRoute requireOrgAdmin><OrgAdmins /></PrivateRoute>}
          />
          <Route
            path="patients"
            element={
              <ClinicOnlyRoute>
                <PatientList />
              </ClinicOnlyRoute>
            }
          />
          <Route path="reports" element={<ClinicOnlyRoute><Reports /></ClinicOnlyRoute>} />
          <Route path="notifications" element={<ClinicOnlyRoute><Notifications /></ClinicOnlyRoute>} />
          <Route
            path="scribe/:patientId"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireProvider>
                  <ScribeSession />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          {/* The old New Session hub. Sessions start from the Patients page now. */}
          <Route path="sessions" element={<Navigate to="/patients" replace />} />
          <Route
            path="drafts"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireProvider>
                  <Drafts />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="sessions/:type"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireProvider>
                  <SessionPreview />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="protocols"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireProvider>
                  <ProtocolLibrary />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="protocols/:id"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireProvider>
                  <ProtocolWorkbench />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="review/:sessionId"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireProvider>
                  <ReviewNote />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="providers"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireAdmin>
                  <ProviderList />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="staff"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireAdmin>
                  <StaffList />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="administrators"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireAdmin>
                  <AdministratorList />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="manage-patients"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireAdminOrStaff>
                  <ManagePatients />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="add-patient"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireProvider>
                  <AddPatient />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="custom-api"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireAdmin>
                  <CustomApi />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="credits"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireAdmin>
                  <Credits />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="appointment-requests"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireAdminOrStaff>
                  <AppointmentRequests />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="queue"
            element={
              <ClinicOnlyRoute>
                <QueuePage />
              </ClinicOnlyRoute>
            }
          />
          <Route
            path="insights"
            element={
              <ClinicOnlyRoute>
                <PrivateRoute requireProvider>
                  <Insights />
                </PrivateRoute>
              </ClinicOnlyRoute>
            }
          />
          <Route path="settings" element={<Settings />} />
        </Route>
        {/* Any unknown URL (e.g. the retired /portals, or a mistyped path) redirects
            to "/" — which resolves to the dashboard if signed in, or /login if not —
            instead of rendering a blank screen. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
